import { AuthError, isEmailNotVerified, login } from '@/lib/auth-service';
import {
  Alert,
  AuthLayout,
  Button,
  Icon,
  LoginOptions,
  TextField,
} from '@pms-digital/design-system';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';

export interface LoginCardProps {
  /** Called once authentication succeeds (after the brief success animation). */
  onSuccess: () => void;
  /** Shown in the form-level alert on mount (e.g. a session-expired notice); cleared on first edit. */
  initialMessage?: string;
  /** Rendered in the card under the form (the hidden Tahiti environment encart). */
  children?: ReactNode;
}

type FieldErrors = {
  email?: string;
  password?: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const COPY = {
  emailEmpty: 'Saisissez votre adresse e-mail.',
  emailInvalid: "Cette adresse e-mail n'est pas valide.",
  passwordEmpty: 'Saisissez votre mot de passe.',
  invalidCredentials: 'E-mail ou mot de passe incorrect. Réessayez.',
  emailNotVerified:
    'Votre adresse e-mail n’est pas encore vérifiée. Consultez votre boîte de réception.',
  genericError: 'Une erreur est survenue. Réessayez dans un instant.',
} as const;

function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  if (email.trim() === '') {
    errors.email = COPY.emailEmpty;
  } else if (!EMAIL_RE.test(email.trim())) {
    errors.email = COPY.emailInvalid;
  }
  if (password === '') {
    errors.password = COPY.passwordEmpty;
  }
  return errors;
}

/** Error status + message for a `TextField`, or nothing when the field is valid. */
function fieldError(message: string | undefined) {
  return message === undefined ? {} : { status: 'error' as const, message };
}

/**
 * Login card — the login screen of the PMS design system as it is (`AuthLayout`
 * with its title, the form-level `Alert`, two `TextField`s and the `Button`),
 * wired to the Tahiti auth service. Handles field validation, form-level
 * errors, the password show/hide toggle (built into `TextField`), a submitting
 * state, and a success state that triggers {@link LoginCardProps.onSuccess}.
 */
export function LoginCard({ onSuccess, initialMessage, children }: LoginCardProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(initialMessage ?? null);
  const [rememberMe, setRememberMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  // `Alert` takes no ref: this wrapper receives the focus when the alert appears.
  const alertRef = useRef<HTMLDivElement>(null);

  // Move focus to the form-level alert when it appears.
  useEffect(() => {
    if (formError !== null) {
      alertRef.current?.focus();
    }
  }, [formError]);

  // Surface a newly-arrived initialMessage even when the card is already mounted
  // — e.g. a 401 fires while the user is sitting on /login, so the route's
  // `reason=expired` (hence this prop) changes without a remount. The useState
  // initializer only reads the prop once, so without this the message wouldn't
  // show. Keyed on the prop so ordinary typing (which clears it) doesn't re-show.
  useEffect(() => {
    if (initialMessage !== undefined) setFormError(initialMessage);
  }, [initialMessage]);

  // Brief success animation, then hand off to the parent for redirection.
  useEffect(() => {
    if (!succeeded) return;
    const timer = window.setTimeout(onSuccess, 900);
    return () => window.clearTimeout(timer);
  }, [succeeded, onSuccess]);

  function revalidateField(field: keyof FieldErrors, nextEmail: string, nextPassword: string) {
    if (!submitted) return;
    const next = validate(nextEmail, nextPassword);
    setFieldErrors((prev) => ({ ...prev, [field]: next[field] }));
  }

  // A pre-filled message (e.g. session expired) is stale as soon as the user
  // starts a fresh attempt; only clear it once so a later submit failure can
  // still set (and keep) its own formError.
  function clearInitialMessage() {
    if (initialMessage !== undefined)
      setFormError((prev) => (prev === initialMessage ? null : prev));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);

    const errors = validate(email, password);
    setFieldErrors(errors);
    if (errors.email !== undefined) {
      emailRef.current?.focus();
      return;
    }
    if (errors.password !== undefined) {
      passwordRef.current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await login(email.trim(), password);
      setSucceeded(true);
    } catch (error) {
      if (isEmailNotVerified(error)) {
        setFormError(COPY.emailNotVerified);
      } else if (error instanceof AuthError) {
        setFormError(COPY.invalidCredentials);
      } else {
        setFormError(COPY.genericError);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fieldsIncomplete = email.trim() === '' || password === '';

  if (succeeded) {
    return (
      <AuthLayout title="connexion réussie">
        <div className="flex flex-col items-center gap-4 pb-8 text-center" aria-live="polite">
          <span className="inline-flex text-bleu-nuit" aria-hidden="true">
            <Icon name="check_circle" size={32} filled />
          </span>
          <p className="m-0 font-light text-gray-600 text-small">redirection vers vos matrices…</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="se connecter"
      subtitle="accédez à vos matrices de survaleur"
      footer={
        <>
          besoin d’aide ? {/* Stub: contacting the admin is not wired up yet. */}
          <button
            type="button"
            onClick={() => undefined}
            className="cursor-pointer border-0 bg-transparent p-0 underline"
          >
            contactez votre administrateur
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {formError !== null && (
          <div tabIndex={-1} ref={alertRef} className="rounded-md">
            <Alert tone="danger">{formError}</Alert>
          </div>
        )}

        <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
          <TextField
            label="adresse e-mail"
            ref={emailRef}
            type="email"
            name="email"
            autoComplete="email"
            value={email}
            {...fieldError(fieldErrors.email)}
            onChange={(e) => {
              setEmail(e.target.value);
              revalidateField('email', e.target.value, password);
              clearInitialMessage();
            }}
          />

          <TextField
            label="mot de passe"
            ref={passwordRef}
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            {...fieldError(fieldErrors.password)}
            onChange={(e) => {
              setPassword(e.target.value);
              revalidateField('password', email, e.target.value);
              clearInitialMessage();
            }}
          />

          {/* Same options as the login before the design system: both stay, whether or not they act yet. */}
          <LoginOptions rememberMe={rememberMe} onRememberMeChange={setRememberMe} />

          <Button
            variant="secondary"
            size="comfortable"
            fullWidth
            type="submit"
            disabled={fieldsIncomplete}
            loading={submitting}
            loadingLabel="connexion…"
          >
            se connecter
          </Button>
        </form>
        {children}
      </div>
    </AuthLayout>
  );
}
